import { useMemo } from "react";
import { CountVizData, CountVizDatum } from "../types";
import { dataVizColors, dataVizColorsLight, dataVizColorsText } from "../common/colors";
import { getPercentage, getVal, colors2 } from "oolib";
import { getTotalCount } from ".";

interface PrepareDataParams {
  _data: any; // this is the input data. it can be of any form and shape
  labelPath: string;
  valuePath: string | string[];
  isBreakdown: boolean;
  showPercent: boolean;
  tooltipLabelsMapping?: { [key: string]: string };
  tooltipLabelsPath: string | string[];
  summarizeAfterIdx?: number;
  colorIdx?: number;
  breakdownDisplayType?: "grouped" | "stacked";
  sortBars?: SortOption; // order of bars (non-breakdown) or of segments within each group (breakdown)
  sortGroups?: SortOption; // order of groups (breakdown only)
  groupsFollowFirst?: boolean; // breakdown: every group takes the first group's segment order instead of sorting on its own
  preserveOrder?: boolean; // If true, skip sorting and preserve the original data order (useful for numeric range histograms)
  isDim1NumberInput?: boolean; // If true, sort breakdown primary groups by sortOrder when Dim1 is NumberInput
  // Per-bar color override keyed by the datum's `value`. When provided (non-
  // breakdown charts only), each bar whose value is in the map gets its mapped
  // color and every other bar is dimmed to grey — used to spotlight a colour-
  // coded subset (e.g. Annotation Explorer's color-code) while greying the rest.
  barColorByValue?: { [value: string]: string };
}

export type SortOption = "desc" | "asc" | "az" | "za";

const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/** Comparator for one of the four sort options over any item shape. */
const makeSorter = <T,>(
  option: SortOption,
  count: (item: T) => number,
  name: (item: T) => string
) => (a: T, b: T): number =>
  option === "asc" ? count(a) - count(b)
  : option === "az" ? byName(name(a), name(b))
  : option === "za" ? byName(name(b), name(a))
  : count(b) - count(a);

const datumCount = (d: CountVizDatum) => d.labels.count;
const barName = (d: CountVizDatum) => String(d.labels?.name ?? "");
const segmentName = (d: CountVizDatum) => String(d.labels?.tooltipLabel ?? "");
const groupCount = (g: CountVizDatum[]) => g.reduce((acc, item) => acc + item.labels.count, 0);
const groupName = (g: CountVizDatum[]) => String(g[0]?.labels?.name ?? "");

interface PreparedData {
  data: CountVizData;
  dataToSummarize: CountVizData;
  dataMaxValue: number;
  isTotalSummarizedCountHigherThanMax?: boolean;
}

const getBarColorForBreakdownData = ({colorIdx, index, breakdownDisplayType}) => {
  const colorArray = breakdownDisplayType === "stacked" ? dataVizColors : dataVizColorsLight;
  return colorArray[(colorIdx + index) % colorArray.length];
};
const getTextColorForBreakdownData = ({colorIdx, index}) => dataVizColorsText[(colorIdx + index) % dataVizColorsText.length];

// Helper function to get identifier using tooltipLabel logic - reused in mapping and final step
const getIdentifierFromPath = (
  d: any, 
  path: string, 
  i: number, 
  tooltipLabelsPath?: string | string[], 
  tooltipLabelsMapping?: { [key: string]: string }
): string => {
  let identifier: string;
  
  if (tooltipLabelsPath) {
    // Use tooltipLabelsPath logic to get the identifier - matches original logic exactly
    if (Array.isArray(tooltipLabelsPath)) {
      // Check if tooltipLabelsPath[i] exists before using it
      identifier = tooltipLabelsPath[i] 
        ? getVal(d, tooltipLabelsPath[i]) || tooltipLabelsPath[i]
        : path; // fallback to path if tooltipLabelsPath[i] doesn't exist
    } else if (typeof tooltipLabelsPath === 'string') {
      identifier = getVal(d, tooltipLabelsPath) || tooltipLabelsPath;
    } else {
      identifier = path;
    }
  } else if (tooltipLabelsMapping) {
    identifier = tooltipLabelsMapping[path] || path;
  } else {
    identifier = path;
  }
  
  return identifier;
};

// Create a consistent mapping of unique identifiers to color indices for breakdown data
const createValueToColorMapping = (
  data: any[], 
  tooltipLabelsPath?: string | string[], 
  valuePath?: string | string[], 
  tooltipLabelsMapping?: { [key: string]: string }
): { [key: string]: number } => {
  const uniqueValues = new Set<string>();

  // Collect identifiers from EVERY row, not just the longest one. Rows are
  // sparse — each holds only the categories present in it — so no single row
  // is guaranteed to contain every category. A category missing from the
  // template row got no mapping and fell back to its positional index at
  // render, which can be the index another category legitimately owns: two
  // categories, one colour.
  data.forEach((d) => {
    const children = Array.isArray(d.children) ? d.children : [];
    (valuePath as string[]).forEach((path: string, i: number) => {
      // Only slots this row actually has. Past the row's children the
      // identifier lookup falls back to the path string itself, which is not
      // a category and would pollute the set.
      if (i >= children.length) return;
      uniqueValues.add(
        getIdentifierFromPath(d, path, i, tooltipLabelsPath, tooltipLabelsMapping)
      );
    });
  });
  
  // Create consistent mapping
  const valueToColorIndex: { [key: string]: number } = {};
  Array.from(uniqueValues).sort().forEach((value: string, index: number) => {
    valueToColorIndex[value] = index;
  });
  
  return valueToColorIndex;
};

/**
 * Sorts every breakdown group's segments into the order the first group gets
 * under `segmentSorter`, so segments line up across groups.
 * @param finalData - Array of breakdown groups, already in display order
 * @returns Sorted breakdown data with consistent label ordering across all groups
 */
const sortBreakdownByLabel = (
  finalData: CountVizDatum[][],
  segmentSorter: (a: CountVizDatum, b: CountVizDatum) => number
): CountVizDatum[][] => {
  const firstGroup = finalData[0] ?? [];
  
  const labelOrder: string[] = [];
  if (firstGroup.length > 0) {
    const sortedFirstGroup = [...firstGroup].sort(segmentSorter);
    
    // Extract unique labels in the order they appear (by count)
    const seenLabels = new Set<string>();
    sortedFirstGroup.forEach((item: CountVizDatum) => {
      const label = String(item.labels?.tooltipLabel || '');
      if (label && !seenLabels.has(label)) {
        labelOrder.push(label);
        seenLabels.add(label);
      }
    });
  }
  
  // Sort all groups using the same label order established from first group
  return finalData.map((innerArray: CountVizDatum[]) => {
    return [...innerArray].sort((a: CountVizDatum, b: CountVizDatum) => {
      const aLabel = String(a.labels?.tooltipLabel || '');
      const bLabel = String(b.labels?.tooltipLabel || '');
      
      const aIndex = labelOrder.indexOf(aLabel);
      const bIndex = labelOrder.indexOf(bLabel);
      
      // If both labels are in the order, sort by position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only one is in the order, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // If neither is in the order, fall back to the segment sorter
      return segmentSorter(a, b);
    });
  });
};

export const usePrepareData = ({
  _data,
  labelPath,
  valuePath,
  tooltipLabelsMapping,
  tooltipLabelsPath,
  isBreakdown,
  showPercent,
  summarizeAfterIdx,
  colorIdx,
  breakdownDisplayType = "stacked",
  sortBars = "desc",
  sortGroups = "desc",
  groupsFollowFirst = false,
  preserveOrder = false,
  isDim1NumberInput = false,
  barColorByValue
}: PrepareDataParams): PreparedData => {

  return useMemo(() => {
    let finalData = [..._data];

    if(finalData.length === 0) return ({data: finalData, dataToSummarize: [], dataMaxValue: 0});

    const valueToColorMapping = isBreakdown ? createValueToColorMapping(finalData, tooltipLabelsPath, valuePath, tooltipLabelsMapping) : {};

    const totalCount = !isBreakdown
      ? getTotalCount({ data: finalData, countPath: valuePath as string })
      : finalData.map((d: any) => {
          const valueSum = (valuePath as string[])
            .map((path) => getVal(d, path as string) || 0) // fallback to 0, cuz sometimes backend systems return 'undefined' if the count of data against a field is non-existent / zero
            .reduce((acc, curr) => acc + curr, 0);
          return valueSum;
        });

    finalData = finalData.map((d: any, index: number) => {
      if (!isBreakdown) {
        const count = getVal(d, valuePath as string) || 0; // fallback to 0, cuz sometimes backend systems return 'undefined' if the count of data against a field is non-existent / zero
        // Preserve existing percentage if it exists (e.g., pre-calculated from original totals)
        const existingPercentage = d.percentage;

        return {
          labels: {
            ...d,
            ...(showPercent
              ? { percentage: existingPercentage ?? getPercentage(count, totalCount as number) }
              : {}),
            count,
            name: getVal(d, labelPath),
            // When a per-value color map is supplied, this bar takes its mapped
            // color if listed, otherwise it's dimmed to grey. Falls back to the
            // normal colorIdx-based palette color when no map is provided.
            barColor: barColorByValue
              ? barColorByValue[String(d.value)] ?? colors2.grey15
              : breakdownDisplayType === "stacked"
                ? dataVizColors[colorIdx % dataVizColors.length]
                : dataVizColorsLight[colorIdx % dataVizColorsLight.length],
            textColor: dataVizColorsText[colorIdx % dataVizColorsText.length]
          },
        };
      }

      const groupArray = (valuePath as string[]).map((path: string, i: number) => {
        const count = getVal(d, path) || 0;
        
        // Get the identifier using the same logic as tooltipLabel for consistent coloring
        const identifier = getIdentifierFromPath(d, path, i, tooltipLabelsPath, tooltipLabelsMapping);
        
        // Use the consistent color mapping instead of array index
        const colorIndex = valueToColorMapping[identifier] !== undefined 
          ? valueToColorMapping[identifier] 
          : i; // fallback to index if identifier not found
        
        /**
         * for breakdown first we assign a color to each valuepath. 
         * and then that color stays consistent for that valuepath across different breakdown groups
         */
        const barColor = getBarColorForBreakdownData({colorIdx, index: colorIndex, breakdownDisplayType})
        const textColor = getTextColorForBreakdownData({colorIdx, index: colorIndex})
        
        // For breakdown charts, check if child has pre-calculated percentage
        // The percentage would be stored in the child object if it exists
        // Extract child index from path (e.g., "children.0.count" -> 0)
        const childIndexMatch = path.match(/children\.(\d+)\.count/);
        const childIndex = childIndexMatch ? parseInt(childIndexMatch[1], 10) : i;
        const child = Array.isArray(d.children) && d.children[childIndex] ? d.children[childIndex] : null;
        const existingPercentage = child?.percentage;
        
        // A breakdown datum labels itself by display, so the id sits at the
        // sibling value: children.N.display -> children.N.value.
        const breakdownKeyPath = (
          Array.isArray(tooltipLabelsPath) ? tooltipLabelsPath[i] : tooltipLabelsPath
        );
        const breakdownValue =
          typeof breakdownKeyPath === "string" && breakdownKeyPath.endsWith("display")
            ? getVal(d, breakdownKeyPath.slice(0, -"display".length) + "value")
            : undefined;

        return {
          labels: {
            ...(showPercent
              ? { percentage: existingPercentage ?? getPercentage(count, totalCount[index]) }
              : {}),
            count,
            tooltipLabel: identifier,
            name: getVal(d, labelPath),
            rowValue: d.value,
            breakdownValue,
            barColor,
            textColor
          },
        };
      });
      
      // Preserve sortOrder on the group array for breakdown chart sorting
      // Only preserve if it's a valid number (not null or undefined)
      if (isBreakdown && typeof d.sortOrder === 'number') {
        (groupArray as any).sortOrder = d.sortOrder;
      }
      
      return groupArray;
    });

    // Sort by total value (skip if preserveOrder is true - e.g., for numeric range histograms)
    if (!preserveOrder) {
      if (isBreakdown && isDim1NumberInput) {
        // For breakdown charts with Dim1 NumberInput, sort primary groups by sortOrder
        // The sortOrder is preserved as a property on each group array
        finalData.sort((a: any, b: any) => {
          // Try to get sortOrder from the array property first
          let aSortOrder = (typeof a.sortOrder === 'number') ? a.sortOrder : null;
          let bSortOrder = (typeof b.sortOrder === 'number') ? b.sortOrder : null;
          
          // Fallback: if sortOrder is not available, try to parse from the first item's name
          // The name can be like "1 - 2", "-5 - 0", or "(50 - 100)" for gap ranges
          // We extract the first number (including negative sign)
          if (aSortOrder === null && a.length > 0 && a[0]?.labels?.name) {
            // Match numbers at the start (including negative sign), or after opening parenthesis for gap ranges
            // Pattern: optional opening paren, optional minus sign, digits, optional decimal
            const match = String(a[0].labels.name).match(/^\(?(-?\d+(?:\.\d+)?)/);
            aSortOrder = match ? parseFloat(match[1]) : Infinity;
          }
          if (bSortOrder === null && b.length > 0 && b[0]?.labels?.name) {
            // Match numbers at the start (including negative sign), or after opening parenthesis for gap ranges
            const match = String(b[0].labels.name).match(/^\(?(-?\d+(?:\.\d+)?)/);
            bSortOrder = match ? parseFloat(match[1]) : Infinity;
          }
          
          // Use Infinity as final fallback
          aSortOrder = aSortOrder ?? Infinity;
          bSortOrder = bSortOrder ?? Infinity;
          
          return aSortOrder - bSortOrder;
        });
      } else if (!isBreakdown) {
        const sorter = makeSorter(sortBars, datumCount, barName);
        finalData.sort((a: any, b: any) => {
          // Keep the combined "Others" slice last regardless of its size.
          if (a.labels.isOthers) return 1;
          if (b.labels.isOthers) return -1;
          return sorter(a, b);
        });
      } else {
        // Orders the whole breakdown groups/stacks
        finalData.sort(makeSorter(sortGroups, groupCount, groupName));
      }
    }

    // Sort segments within each breakdown group (skip if preserveOrder is true)
    if (isBreakdown && !preserveOrder) {
      const segmentSorter = makeSorter(sortBars, datumCount, segmentName);
      finalData = groupsFollowFirst
        ? sortBreakdownByLabel(finalData, segmentSorter)
        : finalData.map((group: CountVizDatum[]) => [...group].sort(segmentSorter));
    }

    finalData = isBreakdown
      ? finalData.map((d: any, i) =>
          d.map((dd: CountVizDatum, ii: number) => ({
            ...dd,
            labels: { ...dd.labels, dataIndex: i },
          }))
        )
      : finalData.map((d: CountVizDatum, i) => ({
          ...d,
          labels: { ...d.labels, dataIndex: i },
        }));

    // Whether bars beyond summarizeAfterIdx collapse into an "Others" bar.
    // Tested as a real number, not for truthiness: 0 is a meaningful threshold
    // ("keep just the first bar") that truthiness would read as "no threshold"
    // and show every bar instead — the exact opposite. undefined means "no
    // preference" (the caller lets the chart decide) and Infinity is the
    // "show all, never summarize" sentinel; Number.isFinite rejects both.
    const shouldSummarize = !isBreakdown && Number.isFinite(summarizeAfterIdx);

    const mainData = shouldSummarize
      ? finalData.filter((_, i) => i <= summarizeAfterIdx)
      : finalData;

    const convertDataToSummrizeIntoBreakdownFormat = (
      data: CountVizDatum[]
    ) => {
      const reshaped = data.map((d: CountVizDatum, i: number) => ({
        ...d,
        labels: {
          ...d.labels,
          name: "Others",
          tooltipLabel: d.labels.name,
          dataIndex: i,
          barColor: getBarColorForBreakdownData({colorIdx: colorIdx+1, index: i, breakdownDisplayType}),
          textColor: getTextColorForBreakdownData({colorIdx: colorIdx+1, index: i})
        },
      }));

      return [reshaped];
    };

    const calcDataMaxValue = (mainData, isBreakdown) => {
      //now if there is no data to summarize its simple. take the first element and get its count (for !isBreakdown) or accumulate to count for first element (isBreakdown)
      // with dataToSummarize, its interesting, because a summarized bar is a 'summation' of everything it is summarizing. sooo.. the summation (and not the individual values) should be taken into consideration while figuring out maxValue
      const getHighestNumInArray = (ary) => {
        let highest = 0;
        ary.forEach(item => {if(item.labels.count > highest) highest = item.labels.count})
          return highest
      }

      const getHighestNumInArrayOfArrays = (ary) => {
        let highest = 0;
        ary.forEach(subAry => {
          const subHighest = getHighestNumInArray(subAry);
          if (subHighest > highest) highest = subHighest
        })
        return highest;
      }

      return !isBreakdown
          ? getHighestNumInArray(mainData) 
          : getHighestNumInArrayOfArrays(mainData);
      };
    
    const totalSummarizedCount = shouldSummarize &&
        finalData.slice(summarizeAfterIdx + 1)
          .map((d: CountVizDatum) => d.labels.count)
          .reduce((acc, curr) => acc + curr, 0)

    // A threshold with fewer bars than the threshold produces no overflow, and
    // convert() would reshape that empty list into a 1-element [[]] — an empty
    // phantom summary bar. Guard on the actual overflow length below so it can't.
    const overflowToSummarize = shouldSummarize
      ? (finalData.filter((_, i) => i > summarizeAfterIdx) as CountVizDatum[])
      : [];
    const dataToSummarize = overflowToSummarize.length > 0
      ? convertDataToSummrizeIntoBreakdownFormat(overflowToSummarize)
      : undefined;
    return {
      data: mainData,
      dataMaxValue: calcDataMaxValue(mainData, isBreakdown),
      dataToSummarize: dataToSummarize,
      isTotalSummarizedCountHigherThanMax: totalSummarizedCount > calcDataMaxValue(mainData, isBreakdown)
    };
  }, [_data, labelPath, valuePath, isBreakdown, showPercent, breakdownDisplayType, sortBars, sortGroups, groupsFollowFirst, preserveOrder, isDim1NumberInput, barColorByValue, summarizeAfterIdx]);
};